import React, { useEffect, useRef, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Eraser,
  Heading,
} from 'lucide-react';
import './RichTextEditor.scss';

/**
 * Lightweight contenteditable rich-text editor used on the job posting form.
 *
 * Why contenteditable + execCommand and not e.g. tiptap or quill?
 *   - Zero new top-level dependencies (only DOMPurify, which we want anyway)
 *   - Output is plain HTML stored in the existing TEXT columns, with no schema
 *     change required
 *   - Works in every evergreen browser today; if execCommand ever truly goes
 *     away, we swap the implementation behind this same component without
 *     touching the call sites.
 *
 * Sanitization happens on every change via `sanitize()` so the value passed to
 * `onChange` is always safe to round-trip through the backend and re-render
 * with `dangerouslySetInnerHTML` on the public job detail page.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h3', 'h4',
  'ul', 'ol', 'li',
  'a', 'span', 'div', 'font',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'color', 'size'];

// Whitelist of style properties we let users set via the toolbar. Anything else
// is stripped, so a paste from Word / Google Docs won't smuggle in fonts that
// break the page or scripts disguised as `style` urls.
const ALLOWED_STYLES = new Set([
  'color',
  'background-color',
  'font-size',
  'font-weight',
  'font-style',
  'text-decoration',
]);

const FONT_SIZE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Mažas', value: '2' },
  { label: 'Įprastas', value: '3' },
  { label: 'Vidutinis', value: '4' },
  { label: 'Didelis', value: '5' },
  { label: 'Labai didelis', value: '6' },
];

const COLOR_PALETTE: string[] = [
  '#1F2937', // ink
  '#13B69B', // brand
  '#0f9a82', // brand dark
  '#2563EB', // blue
  '#DC2626', // red
  '#D97706', // amber
  '#059669', // green
  '#7C3AED', // violet
  '#6B7280', // gray
];

export const sanitizeRichHtml = (dirty: string): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
    KEEP_CONTENT: true,
  }).replace(/style="([^"]*)"/g, (_match, css: string) => {
    const cleaned = css
      .split(';')
      .map(decl => decl.trim())
      .filter(Boolean)
      .filter(decl => {
        const prop = decl.split(':')[0]?.trim().toLowerCase();
        return prop && ALLOWED_STYLES.has(prop);
      })
      .join('; ');
    return cleaned ? `style="${cleaned}"` : '';
  });
};

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  minHeight?: number;
  ariaLabel?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  autoFocus,
  minHeight = 140,
  ariaLabel,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [fontMenuOpen, setFontMenuOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const fontMenuRef = useRef<HTMLDivElement>(null);

  // Seed the editor with the initial value exactly once. Re-syncing `value`
  // from props on every render would obliterate the caret position whenever
  // the parent re-renders mid-typing.
  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = sanitizeRichHtml(value || '');
    }
    if (autoFocus) {
      editorRef.current.focus();
      // Place caret at the end so the user keeps typing where they left off.
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close popovers on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setColorPickerOpen(false);
      }
      if (fontMenuRef.current && !fontMenuRef.current.contains(e.target as Node)) {
        setFontMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const raw = editorRef.current.innerHTML;
    const clean = sanitizeRichHtml(raw);
    onChange(clean);
  }, [onChange]);

  // Strip remote formatting on paste so a user pasting from Word doesn't
  // import 200 lines of MSO `<o:p>` and inline-style noise.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const cleanedHtml = html ? sanitizeRichHtml(html) : '';
    const insert = cleanedHtml || text.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c));
    document.execCommand('insertHTML', false, insert);
    handleInput();
  }, [handleInput]);

  const exec = useCallback((command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    handleInput();
  }, [handleInput]);

  const applyFontSize = (size: string) => {
    exec('fontSize', size);
    setFontMenuOpen(false);
  };

  const applyColor = (color: string) => {
    exec('foreColor', color);
    setColorPickerOpen(false);
  };

  const insertLink = () => {
    const url = window.prompt('Įveskite nuorodą (https://...)');
    if (!url) return;
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    exec('createLink', safe);
    // Force target="_blank" + rel on the freshly inserted anchor; execCommand
    // doesn't expose this option, so we patch it manually.
    if (editorRef.current) {
      editorRef.current.querySelectorAll('a').forEach(a => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });
      handleInput();
    }
  };

  const clearFormatting = () => {
    exec('removeFormat');
    exec('unlink');
  };

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Teksto formatavimas">
        <button type="button" className="rte-btn" title="Paryškintas" onClick={() => exec('bold')}>
          <Bold size={14} />
        </button>
        <button type="button" className="rte-btn" title="Pasviras" onClick={() => exec('italic')}>
          <Italic size={14} />
        </button>
        <button type="button" className="rte-btn" title="Pabrauktas" onClick={() => exec('underline')}>
          <Underline size={14} />
        </button>
        <button type="button" className="rte-btn" title="Perbrauktas" onClick={() => exec('strikeThrough')}>
          <Strikethrough size={14} />
        </button>

        <span className="rte-divider" />

        <button
          type="button"
          className="rte-btn"
          title="Antraštė"
          onClick={() => exec('formatBlock', '<h3>')}
        >
          <Heading size={14} />
        </button>
        <button type="button" className="rte-btn" title="Sąrašas su ženkleliais" onClick={() => exec('insertUnorderedList')}>
          <List size={14} />
        </button>
        <button type="button" className="rte-btn" title="Sąrašas su numeriais" onClick={() => exec('insertOrderedList')}>
          <ListOrdered size={14} />
        </button>

        <span className="rte-divider" />

        <div className="rte-popover" ref={fontMenuRef}>
          <button
            type="button"
            className="rte-btn rte-btn--label"
            title="Šrifto dydis"
            onClick={() => { setFontMenuOpen(v => !v); setColorPickerOpen(false); }}
          >
            <span className="rte-label-letter">A</span>
            <span className="rte-label-arrow">▾</span>
          </button>
          {fontMenuOpen && (
            <div className="rte-menu">
              {FONT_SIZE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className="rte-menu__item"
                  onClick={() => applyFontSize(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rte-popover" ref={colorPickerRef}>
          <button
            type="button"
            className="rte-btn rte-btn--label"
            title="Teksto spalva"
            onClick={() => { setColorPickerOpen(v => !v); setFontMenuOpen(false); }}
          >
            <span className="rte-label-letter rte-label-letter--color">A</span>
            <span className="rte-label-arrow">▾</span>
          </button>
          {colorPickerOpen && (
            <div className="rte-menu rte-menu--colors">
              {COLOR_PALETTE.map(color => (
                <button
                  key={color}
                  type="button"
                  className="rte-color-swatch"
                  style={{ backgroundColor: color }}
                  title={color}
                  onClick={() => applyColor(color)}
                />
              ))}
            </div>
          )}
        </div>

        <span className="rte-divider" />

        <button type="button" className="rte-btn" title="Įterpti nuorodą" onClick={insertLink}>
          <LinkIcon size={14} />
        </button>
        <button type="button" className="rte-btn" title="Pašalinti formatavimą" onClick={clearFormatting}>
          <Eraser size={14} />
        </button>
      </div>

      <div
        ref={editorRef}
        className="rich-text-editor__surface"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel || placeholder}
        data-placeholder={placeholder || ''}
        style={{ minHeight }}
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
      />
    </div>
  );
};

export default RichTextEditor;
